import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Overview - Default landing page
 *
 * Features:
 * - Top KPI cards (5 metrics)
 * - Activity timeline (left)
 * - Usage snapshot (right)
 */

export default function OrgOverview({ organization, members, stats, growth, recentActivity }) {
  const [usageData, setUsageData] = useState({
    meetingTypes: { scheduled: 0, instant: 0 },
    avgDuration: 0,
    guestRatio: 0,
    recordingsCount: 0
  });

  // Calculate usage data
  useEffect(() => {
    if (organization?.id) {
      calculateUsageData();
    }
  }, [organization?.id, stats]);

  const calculateUsageData = async () => {
    try {
      const meetingsRef = collection(db, `organizations/${organization.id}/meetings`);
      const meetingsSnapshot = await getDocs(meetingsRef);

      let scheduled = 0;
      let instant = 0;
      let totalDuration = 0;
      let totalGuests = 0;
      let totalParticipants = 0;

      meetingsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.isScheduled) scheduled++;
        else instant++;
        if (data.duration) totalDuration += data.duration;
        if (data.guestCount) totalGuests += data.guestCount;
        if (data.participantCount) totalParticipants += data.participantCount;
      });

      const recordingsRef = collection(db, `organizations/${organization.id}/recordings`);
      const recordingsSnapshot = await getDocs(recordingsRef);

      setUsageData({
        meetingTypes: { scheduled, instant },
        avgDuration: meetingsSnapshot.size > 0 ? Math.round(totalDuration / meetingsSnapshot.size) : 0,
        guestRatio: totalParticipants > 0 ? Math.round((totalGuests / totalParticipants) * 100) : 0,
        recordingsCount: recordingsSnapshot.size
      });
    } catch (error) {
      console.error('Error calculating usage data:', error);
    }
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Total Meetings */}
        <div className="group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15 p-5 transition-all duration-300 hover:from-white/15 hover:to-white/10 hover:border-[#06c]/40 hover:shadow-lg hover:shadow-blue-500/10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Meetings</div>
            <div className="text-3xl font-bold text-white">{stats?.totalMeetings || 0}</div>
            <div className="flex items-center gap-1.5 mt-2">
              <span className={`w-1.5 h-1.5 rounded-full ${growth?.meetings > 0 ? 'bg-green-400' : 'bg-white/30'}`} />
              <span className={`text-xs font-medium ${growth?.meetings > 0 ? 'text-green-400' : 'text-white/40'}`}>
                {growth?.meetings > 0 ? '+' : ''}{growth?.meetings || 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Meeting Time */}
        <div className="group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15 p-5 transition-all duration-300 hover:from-white/15 hover:to-white/10 hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Total Hours</div>
            <div className="text-3xl font-bold text-white">{Math.floor((stats?.totalMinutes || 0) / 60)}h</div>
            <div className="flex items-center gap-1.5 mt-2">
              <span className={`w-1.5 h-1.5 rounded-full ${growth?.hours > 0 ? 'bg-green-400' : 'bg-white/30'}`} />
              <span className={`text-xs font-medium ${growth?.hours > 0 ? 'text-green-400' : 'text-white/40'}`}>
                {growth?.hours > 0 ? '+' : ''}{growth?.hours || 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Active Members */}
        <div className="group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15 p-5 transition-all duration-300 hover:from-white/15 hover:to-white/10 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/10">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Active</div>
            <div className="text-3xl font-bold text-white">{stats?.activeUsers || 0}</div>
            <div className="text-white/40 text-xs mt-2">of {organization?.seats || 10} seats</div>
          </div>
        </div>

        {/* Guests Hosted */}
        <div className="group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15 p-5 transition-all duration-300 hover:from-white/15 hover:to-white/10 hover:border-orange-500/40 hover:shadow-lg hover:shadow-orange-500/10">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Guests</div>
            <div className="text-3xl font-bold text-white">{usageData.guestRatio}%</div>
            <div className="text-white/40 text-xs mt-2">of participants</div>
          </div>
        </div>

        {/* Recordings */}
        <div className="group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15 p-5 transition-all duration-300 hover:from-white/15 hover:to-white/10 hover:border-pink-500/40 hover:shadow-lg hover:shadow-pink-500/10">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">Recordings</div>
            <div className="text-3xl font-bold text-white">{usageData.recordingsCount}</div>
            <div className="text-white/40 text-xs mt-2">{stats?.storageUsed || 0} GB used</div>
          </div>
        </div>
      </div>

      {/* Two Column Layout: Activity + Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Recent Activity (Left - 3 cols) */}
        <div className="lg:col-span-3 backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-white font-semibold">Recent Org Activity</h2>
          </div>
          <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {recentActivity && recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#06c]/30 to-purple-500/30 flex items-center justify-center text-lg">
                    {activity.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm">
                      <span className="font-medium">{activity.user?.split('@')[0] || 'Unknown'}</span>
                      <span className="text-white/60"> {activity.action}</span>
                    </div>
                    <div className="text-white/40 text-xs mt-0.5">{formatRelativeTime(activity.timestamp)}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">📊</div>
                <div className="text-white/50 text-sm">No recent activity</div>
                <div className="text-white/30 text-xs mt-1">Activity will appear as your team uses QuickMeet</div>
              </div>
            )}
          </div>
        </div>

        {/* Usage Snapshot (Right - 2 cols) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Meeting Types */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-5">
            <h3 className="text-white/70 text-xs font-medium uppercase tracking-wider mb-4">Meeting Types</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Scheduled</span>
                <span className="text-white font-medium">{usageData.meetingTypes.scheduled}</span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#06c] to-blue-400 rounded-full transition-all duration-500"
                  style={{
                    width: `${stats?.totalMeetings ? (usageData.meetingTypes.scheduled / stats.totalMeetings) * 100 : 0}%`
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Instant</span>
                <span className="text-white font-medium">{usageData.meetingTypes.instant}</span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${stats?.totalMeetings ? (usageData.meetingTypes.instant / stats.totalMeetings) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-5">
            <h3 className="text-white/70 text-xs font-medium uppercase tracking-wider mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Avg Meeting Duration</span>
                <span className="text-white font-medium">{usageData.avgDuration}m</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Storage Limit</span>
                <span className="text-white font-medium">{stats?.storageLimit || 1000} GB</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Team Size</span>
                <span className="text-white font-medium">{members?.length || 0} members</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15 hover:from-white/15 hover:to-white/10 hover:border-white/25 transition-all">
              <span className="text-2xl">📅</span>
              <span className="text-white/70 text-xs font-medium">Schedule</span>
            </button>
            <button className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15 hover:from-white/15 hover:to-white/10 hover:border-white/25 transition-all">
              <span className="text-2xl">👥</span>
              <span className="text-white/70 text-xs font-medium">Invite</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
